import { useTranslation } from 'react-i18next';

type HelpTextType =
  | 'main'
  | 'resources'
  | 'plants'
  | 'factories'
  | 'stock'
  | 'partners'
  | 'friends'
  | 'earn'
  | 'wood'
  | 'cotton'
  | 'bulk'
  | 'lumber'
  | 'textiles'
  | 'bricks'
  | 'paper'
  | 'furniture_factory'
  | 'garment_factory'
  | 'printing_factory'
  | 'build_upgrade'
  | 'boosts';

type HelpTextItem = {
  title: string;
  paragraf: string[];
  addition?: string[];
};

type HelpTextStructure = {
  [key in HelpTextType]: {
    [id: string]: HelpTextItem;
  };
};

function HelpTextConfig(): HelpTextStructure {
  const { t } = useTranslation();

  const helpText: HelpTextStructure = {
    main: {
      '1': {
        title: t(
          'To level up your account and grow your empire, follow the hint – it will quickly take you to the building where you can see what you need to gather for its construction or upgrade.',
        ),
        paragraf: [
          t('📌 What to do:'),
          t(
            '✅ Visit the Friends, Earn, and Rating sections to compete and earn extra income.',
          ),
          t('✅ Complete contracts – they grant Star Points and more CASH.'),
          t(
            '✅ Open reward containers – they speed up progress, help complete contracts, and accelerate your growth.',
          ),
        ],
      },
      '2': {
        title: t(
          'To maximize the growth of your industry, it’s essential to manage contracts and rewards wisely.',
        ),
        paragraf: [
          t('📌 How to earn more Star Points:'),
          t(
            '✅ Manage contracts – check which components are missing and adjust production accordingly.',
          ),
          t(
            '✅ The harder the contracts, the more Star Points you earn. Advanced orders require more effort but offer greater rewards.',
          ),
          t(
            '✅ You can decline contracts if they are unprofitable. Choose contracts that align with your strategy and current resources.',
          ),
        ],
      },
    },
    resources: {
      '1': {
        title: t(
          'This panel displays the management of resource-extraction enterprises.',
        ),
        paragraf: [
          t('📌 Key points:'),
          t('✅ There are 6 types of resources available.'),
          t('✅ They unlock gradually as you progress in the game.'),
          t(
            '✅ Manage extraction, collect resources on time, and send them to storage.',
          ),
        ],
      },
    },
    plants: {
      '1': {
        title: t(
          'Here, we manage the production of materials needed for crafting goods and fulfilling contracts. Plan your resource allocation wisely!',
        ),
        paragraf: [
          t('📌 Key points:'),
          t(
            '✅ There are 9 types of materials in the game – as you progress from building to building, you’ll unlock them all.',
          ),
          t(
            '✅ Manage your stock – keep track of how many components are needed for contracts and further production.',
          ),
          t(
            '✅ Maintain material balance – smart resource distribution prevents downtime and speeds up development.',
          ),
        ],
      },
    },
    factories: {
      '1': {
        title: t(
          'Here, you’ll find all the factories where goods are produced. Manufacturing requires materials, with each product needing between 1 to 6 components.',
        ),
        paragraf: [
          t('📌 How production works:'),
          t('✅ Each factory can produce up to 4 goods.'),
          t('✅ Goods unlock as the factory is upgraded:'),
          t('• Level 1 – First product.'),
          t('• Level 5 – Second product.'),
          t('• Level 10 – Third product.'),
          t('• Level 15 – Fourth product.'),
          t(
            '✅ Upgrading factories speeds up production and increases the queue size, allowing more goods to be processed at once.',
          ),
        ],
      },
    },
    stock: {
      '1': {
        title: t(
          'The Stock is where we store resources, materials, and goods. Efficient inventory management is key to keeping production running smoothly.',
        ),
        paragraf: [
          t('📌 Key points:'),
          t(
            '✅ You can sell items from the stock, but it\ns more profitable to use resources, materials, and goods to complete contracts. This way, you earn more CASH and receive additional rewards from containers.',
          ),
          t(
            '✅ Goods are the most valuable component – they generate higher profits and grant more Star Points when fulfilling contracts.',
          ),
        ],
      },
    },
    partners: {
      '1': {
        title: t('The partnership program is a way to earn passive income.'),
        paragraf: [
          t('📌 How to earn:'),
          t('✅ Invite friends using your referral link.'),
          t('✅ The higher their level, the bigger your reward.'),
          t('✅ Rewards are given in CASH, T-Gem and TON.'),
        ],
      },
    },
    friends: {
      '1': {
        title: t(
          'Business is not just about production; it’s also about competition. Add friends, monitor their progress, and prepare for future challenges!',
        ),
        paragraf: [
          t('📌 How the ranking works:'),
          t('✅ The main indicator is your account level.'),
          t(
            '✅ If levels are the same, the winner is the one with more Star Points in the last 15 days.',
          ),
        ],
        addition: [
          t('📌 What’s next?'),
          t(
            '🚀 Team competitions will be added in the future, where teamwork will be essential. Start building a strong team now!',
          ),
          t(
            '🤝 New interaction mechanics with friends will be introduced – the more active partners you have, the more opportunities will open up.',
          ),
        ],
      },
    },
    earn: {
      '1': {
        title: t('Earn T-Gem by completing simple tasks'),
        paragraf: [
          t('📌 How to get bonuses:'),
          t('✅ Subscribe to channels and receive T-Gem.'),
          t('✅ Keep an eye on new tasks in this section.'),
          t(
            '✅ Use the T-Gem you’ve earned to speed up production and improve your business.',
          ),
        ],
      },
    },
    wood: {
      '1': {
        title: t(
          'Wood is one of the basic resources used to produce planks and will later be required for creating other materials. Extraction happens automatically, but you can speed it up with boosts.',
        ),
        paragraf: [
          t('📌 How to manage extraction:'),
          t(
            '✅ Extracted resources are stored in the storage – if it fills up, extraction will stop.',
          ),
          t('✅ Regularly transfer supplies to the Stock to prevent downtime.'),
          t(
            '✅ Use the TAP mechanic to speed up the process and gather more wood.',
          ),
        ],
      },
    },
    cotton: {
      '1': {
        title: t(
          'Here, we extract Cotton, one of the key resources. It will serve as the foundation for producing fabrics, which are needed in later processes.',
        ),
        paragraf: [
          t('📌 Important to remember:'),
          t(
            '✅ Cotton is extracted automatically, but its storage is limited.',
          ),
          t(
            '✅ Regularly send it to the stock – if the storage fills up, extraction will stop.',
          ),
        ],
      },
    },
    bulk: {
      '1': {
        title: t(
          'Here, we extract Bulk Materials, one of the key resources. It will serve as the foundation for producing bricks, which are needed in later processes.',
        ),
        paragraf: [
          t('📌 Important to remember:'),
          t(
            '✅ Bulk materials are extracted automatically, but their storage is limited.',
          ),
          t(
            '✅ Regularly send them to the stock – if the storage fills up, extraction will stop.',
          ),
        ],
      },
    },
    lumber: {
      '1': {
        title: t(
          'Here, we process wood into planks, which are essential for crafting goods and constructing buildings.',
        ),
        paragraf: [
          t('📌 How to start production:'),
          t(
            '✅ Adjust the production volume by moving the slider – this helps plan workload flexibly.',
          ),
          t('✅ Press “Produce”, and the factory will start processing wood.'),
        ],
        addition: [
          t('📌 What the slider colors mean:'),
          t(
            '🟢 Green – Finished planks, ready to be collected in the warehouse.',
          ),
          t('🔵 Dark blue – Materials currently in production.'),
          t(
            '🔹 Light blue – Planned materials that haven’t started yet. Once you press “Produce”, light blue turns dark blue, indicating that production is in progress.',
          ),
        ],
      },
    },
    textiles: {
      '1': {
        title: t(
          'Here, we turn cotton into Textile, which is essential for creating many products.',
        ),
        paragraf: [
          t('📌 How it works:'),
          t(
            '✅ You need to start production manually – it won’t begin without your command.',
          ),
          t(
            '✅ Start the process by pressing the “Produce” button, and the factory will begin converting all available cotton into fabric.',
          ),
          t('✅ Send the finished textile to the stock for further use.'),
        ],
      },
    },
    bricks: {
      '1': {
        title: t(
          'Here, we turn Bulk Materials into Bricks, which are essential for building new enterprises and fulfilling contracts.',
        ),
        paragraf: [
          t('📌 How it works:'),
          t(
            '✅ You need to start production manually – it won’t begin without your command.',
          ),
          t(
            '✅ Start the process by pressing the “Produce” button, and the factory will begin converting bulk materials into bricks.',
          ),
          t('✅ Send the finished bricks to the stock for further use.'),
        ],
      },
    },
    paper: {
      '1': {
        title: t(
          'Here, we turn wood into Paper, which is essential for creating many products.',
        ),
        paragraf: [
          t('📌 How it works:'),
          t(
            '✅ You need to start production manually – it won’t begin without your command.',
          ),
          t(
            '✅ Start the process by pressing the “Produce” button, and the factory will begin converting all available wood into paper.',
          ),
          t('✅ Send the finished paper to the stock for further use.'),
        ],
      },
    },
    furniture_factory: {
      '1': {
        title: t(
          'At this factory, we produce various types of furniture. Currently, Case Furniture is available, with more products coming in the future.',
        ),
        paragraf: [
          t('📌 How production works:'),
          t(
            '✅ Lumber are required to produce case furniture – without them, production will stop.',
          ),
          t(
            '✅ Start the process manually by pressing “Produce” to begin manufacturing.',
          ),
          t(
            '✅ Use boosts to speed up furniture production and increase output.',
          ),
        ],
      },
    },
    garment_factory: {
      '1': {
        title: t(
          'At the Garment Factory, we turn fabric into clothing. It’s important to monitor your stock to keep production running smoothly.',
        ),
        paragraf: [
          t('📌 Control the process:'),
          t(
            '✅ Watch the “Textile” icon – it shows how much material is needed to produce one item and how much fabric is in storage.',
          ),
          t(
            '✅ If the icon is red, it means you’re out of stock. Click on the fabric icon to quickly go to the Textile Factory and start producing more fabric.',
          ),
          t(
            '✅ Start the process – press the “Produce” button, and the factory will begin production.',
          ),
          t('✅ Use boosts – they help speed up clothing production.'),
        ],
      },
    },
    printing_factory: {
      '1': {
        title: t(
          'At the Printing Factory, we turn paper into products. It’s important to monitor your stock to keep production running smoothly.',
        ),
        paragraf: [
          t('📌 Control the process:'),
          t(
            '✅ Watch the “Paper” icon – it shows how much material is needed to produce one item and how much paper is in storage.',
          ),
          t(
            '✅ If the icon is red, it means you’re out of stock. Click on the paper icon to quickly go to the Paper Factory and start producing more paper.',
          ),
          t(
            '✅ Start the process – press the “Produce” button, and the factory will begin production.',
          ),
          t('✅ Use boosts – they help speed up product output.'),
        ],
      },
    },
    build_upgrade: {
      '1': {
        title: t(
          'In this section, we can build new buildings or upgrade existing ones to increase production speed and efficiency.',
        ),
        paragraf: [
          t('📌 What’s important:'),
          t(
            '✅ Check your resources: make sure you have enough CASH and materials for the upgrade.',
          ),
          t(
            '✅ Consider dependencies: some buildings require the prior construction or upgrade of other structures to unlock further development.',
          ),
        ],
        addition: [
          t('📌 Tip:'),
          t(
            'To quickly find a building that you can construct, keep an eye on the tasks in the office. You’ll always find up-to-date information on which building to construct next.',
          ),
        ],
      },
    },
    boosts: {
      '1': {
        title: t(
          'Boosts accelerate production where it’s needed, helping maintain a steady flow of materials and goods. Use them wisely!',
        ),
        paragraf: [
          t('🚀 How boosts work:'),
          t('🟣 Up to 4 boosts per building.'),
          t('🟣 Boosts stack up to x8 speed.'),
          t(
            '🟣 Local boost for factories using T-Gem – speeds up only that specific building, without affecting others.',
          ),
          t(
            '🟣 General boost – accelerates all buildings at once, but with a 20% discount.',
          ),
          t(
            '🟣 Extending a Local boost or General boost with T-Gem increases the duration but not the effect.',
          ),
        ],
      },
    },
  };

  return helpText;
}

export { HelpTextConfig, type HelpTextStructure, type HelpTextType };
